import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { springPresets } from '@/lib/motion';
import { TrendingUp, TrendingDown, Activity, Expand, X } from 'lucide-react';
interface ChartsProps {
  chartData: any;
}
export function Charts({
  chartData
}: ChartsProps) {
  const [modalChart, setModalChart] = useState<string | null>(null);
  const openModal = (chartType: string) => {
    setModalChart(chartType);
  };
  const closeModal = () => {
    setModalChart(null);
  };
  return <>
    <section className="w-full py-12 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Activity className="w-8 h-8 text-primary" />
              2026 Market Key Indicators
            </h2>
            <p className="text-muted-foreground mt-1">
              Real-time monitoring and forecast data based on Q1 2026
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm font-medium">
            
            
            
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Road Freight Growth Chart */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={springPresets.gentle}>
            <Card className="overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openModal('road')}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">European Road Freight Market Indices</CardTitle>
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <CardDescription>Capacity & Contract Price Index (Feb 2021 - Feb 2026)</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.roadFreightIndices} margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 25
                    }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 9
                      }} angle={-45} textAnchor="end" height={60} interval={5} />
                      <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[80, 135]} />
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[1200, 2100]} />
                      <Tooltip contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }} formatter={(value: any, name: string) => {
                        const indexNames: {
                          [key: string]: string;
                        } = {
                          capacity: 'Capacity Index',
                          contractPrice: 'Contract Price Index',
                          dieselPrice: 'Diesel Price (€/1000L)'
                        };
                        return [name === 'dieselPrice' ? `€${value}` : `${value}`, indexNames[name] || name];
                      }} labelFormatter={label => `Month: ${label}`} />
                      <Line type="monotone" dataKey="capacity" yAxisId="left" stroke="var(--chart-1)" strokeWidth={3} dot={false} activeDot={{
                        r: 5,
                        strokeWidth: 0,
                        fill: 'var(--chart-1)'
                      }} name="capacity" />
                      <Line type="monotone" dataKey="contractPrice" yAxisId="left" stroke="var(--chart-2)" strokeWidth={3} dot={false} activeDot={{
                        r: 5,
                        strokeWidth: 0,
                        fill: 'var(--chart-2)'
                      }} name="contractPrice" />
                      <Line type="monotone" dataKey="dieselPrice" yAxisId="right" stroke="var(--chart-3)" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{
                        r: 4,
                        strokeWidth: 0,
                        fill: 'var(--chart-3)'
                      }} name="dieselPrice" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Ocean Rate Trend Chart */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...springPresets.gentle,
            delay: 0.1
          }} className="">
            <Card className="overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openModal('ocean')}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold">Ocean Freight Rate Index (SCFI)</CardTitle>
                    <CardDescription>
                      Quarterly Summary 2021-2026
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-rose-500" />
                    <button onClick={e => {
                      e.stopPropagation();
                      openModal('ocean');
                    }} className="p-2 rounded-md hover:bg-muted transition-colors" title="Open in Popup">
                      <Expand className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="w-full h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.oceanRatesTrend} margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 25
                    }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 10
                      }} angle={-45} textAnchor="end" height={60} />
                      <YAxis axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[1200, 4500]} />
                      <Tooltip contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }} formatter={(value: any, name: string, props: any) => {
                        const routeNames: {
                          [key: string]: string;
                        } = {
                          composite: 'Composite Index',
                          europe: 'Europe Route',
                          mediterranean: 'Mediterranean Route'
                        };
                        return [`${value} pts`, routeNames[name] || name, `Period: ${props.payload.period}`];
                      }} labelFormatter={label => `Quarter: ${label}`} />
                      <Line type="monotone" dataKey="composite" stroke="var(--chart-1)" strokeWidth={3} dot={false} activeDot={{
                        r: 6,
                        strokeWidth: 0,
                        fill: 'var(--chart-1)'
                      }} name="composite" />
                      <Line type="monotone" dataKey="europe" stroke="var(--chart-2)" strokeWidth={3} dot={false} activeDot={{
                        r: 6,
                        strokeWidth: 0,
                        fill: 'var(--chart-2)'
                      }} name="europe" />
                      <Line type="monotone" dataKey="mediterranean" stroke="var(--chart-3)" strokeWidth={3} dot={false} activeDot={{
                        r: 6,
                        strokeWidth: 0,
                        fill: 'var(--chart-3)'
                      }} name="mediterranean" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

              </CardContent>
            </Card>
          </motion.div>

          {/* Air Capacity Growth Chart */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...springPresets.gentle,
            delay: 0.2
          }}>
            <Card className="overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openModal('air')}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Inflation Rate</CardTitle>
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <CardDescription>Monthly Inflation Rate % (Feb 2021 - Feb 2026)</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.cpiData} margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 25
                    }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 9
                      }} angle={-45} textAnchor="end" height={60} interval={5} />
                      <YAxis axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[0, 12]} />
                      <Tooltip contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }} formatter={(value: any, name: string) => [`${value}%`, 'Inflation Rate']} labelFormatter={label => `Month: ${label}`} />
                      <Line type="monotone" dataKey="cpi" stroke="var(--chart-3)" strokeWidth={3} dot={false} activeDot={{
                        r: 5,
                        strokeWidth: 0,
                        fill: 'var(--chart-3)'
                      }} name="cpi" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        {/* Additional Charts Row - Hidden */}
        <div className="hidden grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Transport Emissions Chart */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...springPresets.gentle,
            delay: 0.3
          }}>
            <Card className="overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openModal('transport')}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">EU Transport Mode Analysis</CardTitle>
                  <Activity className="w-5 h-5 text-accent" />
                </div>
                <CardDescription>Activity Share vs Emissions Share (2023)</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.transportEmissions} margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 25
                    }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="mode" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 10
                      }} angle={-45} textAnchor="end" height={60} />
                      <YAxis axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[0, 80]} />
                      <Tooltip contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }} formatter={(value, name) => [`${value}%`, name === 'share' ? 'Activity Share' : 'Emissions Share']} />
                      <Bar dataKey="share" fill="var(--chart-1)" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="emissions" fill="var(--chart-2)" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Container Growth Chart */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...springPresets.gentle,
            delay: 0.4
          }}>
            <Card className="overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-300 cursor-pointer" onClick={() => openModal('container')}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Container Trade Growth 2026</CardTitle>
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <CardDescription>Growth Rate by Trade Route (%)</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData.containerGrowth} margin={{
                      top: 5,
                      right: 5,
                      left: 5,
                      bottom: 25
                    }}>
                      <defs>
                        <linearGradient id="containerGrowth" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="tradeRoute" axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 9
                      }} angle={-45} textAnchor="end" height={60} />
                      <YAxis axisLine={false} tickLine={false} tick={{
                        fill: 'var(--muted-foreground)',
                        fontSize: 12
                      }} domain={[-3, 7]} />
                      <Tooltip contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }} formatter={(value, name, props) => [`${value}%`, 'Growth Rate', `Volume: ${props.payload.volume}K TEU`, `Trend: ${props.payload.trend}`]} />
                      <Area type="monotone" dataKey="growth" stroke="var(--accent)" fillOpacity={1} fill="url(#containerGrowth)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-6 mb-4 md:mb-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">CPI Index:</span>
              <span className="font-mono">2.1% (Stable)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">Carbon Costs:</span>
              <span className="font-mono">+14% (Rising)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">Container Growth:</span>
              <span className="font-mono">3% (2026 Forecast)</span>
            </div>
          </div>
          <p>© 2026 Strategic Logistics Intelligence</p>
        </div>
      </div>
    </section>

    {/* Modal for Expanded Charts */}
    <Dialog open={modalChart !== null} onOpenChange={closeModal}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>
              {modalChart === 'ocean' && 'Ocean Freight Rate Index (SCFI) - Detailed View'}
              {modalChart === 'road' && 'European Road Freight Market Indices - Detailed View'}
              {modalChart === 'air' && 'Inflation Rate - Detailed View'}

            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-6">
          {modalChart === 'ocean' && <div className="space-y-4">
              <div className="h-[500px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.oceanRatesModalData} margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 10
                  }} angle={-45} textAnchor="end" height={80} interval={3} />
                    <YAxis axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} domain={[900, 5000]} />
                    <Tooltip contentStyle={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }} formatter={(value: any, name: string) => {
                    const routeNames: {
                      [key: string]: string;
                    } = {
                      composite: 'Composite Index',
                      europe: 'Europe Route',
                      mediterranean: 'Mediterranean Route'
                    };
                    return [`${value} pts`, routeNames[name] || name];
                  }} labelFormatter={label => `Period: ${label}`} />
                    <Line type="monotone" dataKey="composite" stroke="var(--chart-1)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-1)'
                  }} name="composite" />
                    <Line type="monotone" dataKey="europe" stroke="var(--chart-2)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-2)'
                  }} name="europe" />
                    <Line type="monotone" dataKey="mediterranean" stroke="var(--chart-3)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-3)'
                  }} name="mediterranean" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-6 text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-[var(--chart-1)]"></div>
                  <span>Composite Index</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-[var(--chart-2)]"></div>
                  <span>Europe Route</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-[var(--chart-3)]"></div>
                  <span>Mediterranean Route</span>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-medium">Mixed View: Quarterly (2021-2024) + Weekly (2025-2026)</p>
                  <p>Latest: 26.W6 - Composite: 1,267 pts (-3.8% WoW) | Europe: 1,403 pts (-1.1% WoW) | Mediterranean: 2,291 pts (-5.5% WoW)</p>
                  <p className="text-xs mt-1">Data Points: 16 Quarters + 58 Weeks | Peak: 2022 Q1 (Composite: 4,851 pts) | Bottom: 2023 Q1 (Composite: 969 pts)</p>
                </div>
              </div>
            </div>}

          {modalChart === 'road' && <div className="space-y-4">
              <div className="h-[500px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.roadFreightIndices} margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 10
                  }} angle={-45} textAnchor="end" height={80} interval={2} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} domain={[80, 135]} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} domain={[1200, 2100]} />
                    <Tooltip contentStyle={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }} formatter={(value: any, name: string) => {
                    const indexNames: {
                      [key: string]: string;
                    } = {
                      capacity: 'Capacity Index',
                      contractPrice: 'Contract Price Index',
                      dieselPrice: 'Diesel Price (€/1000L)'
                    };
                    return [name === 'dieselPrice' ? `€${value}` : `${value}`, indexNames[name] || name];
                  }} labelFormatter={label => `Month: ${label}`} />
                    <Line type="monotone" dataKey="capacity" yAxisId="left" stroke="var(--chart-1)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-1)'
                  }} name="capacity" />
                    <Line type="monotone" dataKey="contractPrice" yAxisId="left" stroke="var(--chart-2)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-2)'
                  }} name="contractPrice" />
                    <Line type="monotone" dataKey="dieselPrice" yAxisId="right" stroke="var(--chart-3)" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{
                    r: 5,
                    strokeWidth: 0,
                    fill: 'var(--chart-3)'
                  }} name="dieselPrice" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend and Data Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Legend</h4>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--chart-1)]"></div>
                    <span className="text-sm">Capacity Index</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--chart-2)]"></div>
                    <span className="text-sm">Contract Price Index</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-1 bg-[var(--chart-3)]" style={{
                    borderStyle: 'dashed'
                  }}></div>
                    <span className="text-sm">Diesel Price (€/1000L)</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Key Insights</h4>
                  <p className="text-sm text-muted-foreground">
                    Latest Data: Feb 2026 - Capacity: 96.26, Contract Price: 130.90, Diesel: €1,582.62<br />
                    Peak Capacity: Feb 2023 (112.78) | Peak Price: Jan 2026 (131.38) | Peak Diesel: Jun 2022 (€1,988.12)<br />
                    Low Capacity: May 2022 (85.92) | Low Price: Feb 2021 (98.16) | Low Diesel: Feb 2021 (€1,243.29)
                  </p>
                </div>
              </div>
            </div>}

          {modalChart === 'air' && <div className="space-y-4">
              <div className="h-[500px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.cpiData} margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 10
                  }} angle={-45} textAnchor="end" height={80} interval={2} />
                    <YAxis axisLine={false} tickLine={false} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} domain={[0, 12]} />
                    <Tooltip contentStyle={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }} formatter={(value: any, name: string) => [`${value}%`, 'Inflation Rate']} labelFormatter={label => `Month: ${label}`} />
                    <Line type="monotone" dataKey="cpi" stroke="var(--chart-3)" strokeWidth={3} dot={false} activeDot={{
                    r: 6,
                    strokeWidth: 0,
                    fill: 'var(--chart-3)'
                  }} name="cpi" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend and Data Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Legend</h4>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[var(--chart-3)]"></div>
                    <span className="text-sm">Monthly Inflation Rate %</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Key Insights</h4>
                  <p className="text-sm text-muted-foreground">
                    Latest Data: Feb 2026 - Inflation Rate: 2.1%<br />
                    Peak Inflation: Mar 2022 (8.9%) | Current: 2.1% (Target Range)<br />
                    Trend: Stabilized at target level | Status: Controlled inflation
                  </p>
                </div>
              </div>
            </div>}




        </div>
      </DialogContent>
    </Dialog>
    </>;
}